import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Breadcrums from "../../../common/breadcrumbs";
import * as learnHubServices from "../../../services/learnHub.services";
import { SWAL_SETTINGS } from "../../../../../utils/Constants";
import { getChunks, globalLoader, handleServerValidations } from "../../../../../utils/commonfunction";
import CustomCkeditor from "../../../common/customeditor";

const AddLearnHub = () => {
  const navigate = useNavigate();
  const [previewThumbnail, setPreviewThumbnail] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const breadcrumbs = [
    { title: "Dashboard", url: "/admin/dashboard" },
    { title: "Learn Hub", url: "/admin/cms/learn-hub/list/1" },
    { title: "Add Content", url: "" },
  ];

  const validationSchema = Yup.object().shape({
      content_type: Yup.string().oneOf(["article", "lesson"]).required("Select content type"),
      consumer_type: Yup.string().oneOf(["user", "astrologer"]).required("Select Consumer type"),
      title: Yup.string().trim().required("Title is required").max(200, "Title too long"),
      description: Yup.string().trim().required("Description is required"),
      thumbnail: Yup.mixed()
      .required("Thumbnail is required")
      .test(
        "fileType",
        "Only image files are allowed",
        (value) => {
          if (!value) return false; // required already handles this
          return value.type.startsWith("image/");
        }
      ),
      // video_source: Yup.string().when("content_type", {
      //   is: "lesson",
      //   then: (schema) => schema.required("Choose video source"),
      // }),
      // video_file: Yup.mixed().when(["content_type", "video_source"], {
      //   is: (type, source) => type === "lesson" && source === "upload",
      //   then: (schema) => schema.required("Upload a video file"),
      //   otherwise: (schema) => schema.nullable(),
      // }),
      video_source: Yup.string().when("content_type", {
          is: "lesson",
          then: (schema) => schema.required("Choose video source"),
          otherwise: (schema) => schema.nullable(),
      }),

      video_file: Yup.mixed()
          .nullable()
          .when(["content_type", "video_source"], {
              is: (type, source) => type === "lesson" && source === "upload",
              then: (schema) =>
                  schema
                      .required("Please upload a video file")
                      .test("is-mp4-type", "Only MP4 video files are allowed", (value) => {
                          return value && value.type === "video/mp4";
                      })
                      .test("is-mp4-ext", "File must have .mp4 extension", (value) => {
                          return value && value.name.toLowerCase().endsWith(".mp4");
                      }),
              otherwise: (schema) => schema.nullable(),
          }),

      video_link: Yup.string().when(["content_type", "video_source"], {
          is: (type, source) => type === "lesson" && source === "youtube",
          then: (schema) => schema.url("Invalid URL").required("YouTube link required"),
          otherwise: (schema) => schema.nullable(),
      }),
  });

  const formik = useFormik({
    initialValues: {
      content_type: "article",
      consumer_type: "user",
      title: "",
      description: "",
      thumbnail: null,
      video_source: "",
      video_file: null,
      video_link: "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      globalLoader(true);
      setUploading(true);
      setUploadProgress(0);

      try {
        const formData = new FormData();
        formData.append("content_type", values.content_type);
        formData.append("consumer_type", values.consumer_type);
        formData.append("title", values.title.trim());
        formData.append("description", values.description);
        formData.append("thumbnail", values.thumbnail);

        // Always send video_source when content_type is lesson
        if (values.content_type === "lesson") {
          formData.append("video_source", values.video_source);
        }

        // Upload video (only if upload selected)
        if (values.content_type === "lesson" && values.video_source === "upload" && values.video_file) {
          const chunks = await getChunks(values.video_file);
          const total = chunks.length;
          const timestamp = Date.now();

          for (let i = 0; i < chunks.length; i++) {
            const chunk = chunks[i];
            const chunkData = new FormData();
            chunkData.append("name", `${timestamp}_${values.video_file.name}`);
            chunkData.append("size", values.video_file.size);
            chunkData.append("type", values.video_file.type);
            chunkData.append("allChunks", total);
            chunkData.append("currentChunk", i);
            chunkData.append("file", chunk);

            const res = await learnHubServices.UploadChunk(chunkData);
            if (!res?.success) throw new Error("Video upload failed");

            if (i === total - 1) {
              formData.append("file_id", res.data.file_id);
              formData.append("video_filename", `${timestamp}_${values.video_file.name}`);
            }
            setUploadProgress(Math.round(((i + 1) / total) * 100));
          }
        }

        // YouTube link
        if (values.content_type === "lesson" && values.video_source === "youtube") {
          formData.append("video_link", values.video_link);
        }

        const response = await learnHubServices.Add(formData);
        if (response.success) {
          Swal.fire({ icon: "success", text: response.message, ...SWAL_SETTINGS });
          setTimeout(() => navigate("/admin/cms/learn-hub/list/1"), 1500);
        } else {
          throw new Error(handleServerValidations(response));
        }
      } catch (err) {
        Swal.fire({ icon: "error", text: err.message || "Failed to save", ...SWAL_SETTINGS });
      } finally {
        setUploading(false);
        globalLoader(false);
        setSubmitting(false);
      }
    },
  });

  console.log("formik values===>", formik.values);

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <form onSubmit={formik.handleSubmit}>
        <div className="row">
          <div className="col-12">
            <div className="card shadow-sm">
              <div className="card-body">
                <h4 className="mb-4 text-primary">Add New Learn Hub Content</h4>

                {/* Content Type & Consumer Type */}
                <div className="row mb-4">
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Content Type <span className="text-danger">*</span></label>
                    <select
                      className="form-select"
                      value={formik.values.content_type}
                      onChange={(e) => {
                        formik.setFieldValue("content_type", e.target.value);
                        formik.setFieldValue("video_source", "");
                        formik.setFieldValue("video_file", null);
                        formik.setFieldValue("video_link", "");
                      }}
                    >
                      <option value="article">Article</option>
                      <option value="lesson">Video Lesson</option>
                    </select>
                    {formik.touched.content_type && formik.errors.content_type && (
                      <small className="text-danger">{formik.errors.content_type}</small>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Consumer Type <span className="text-danger">*</span></label>
                    <select className="form-select" {...formik.getFieldProps("consumer_type")}>
                      <option value="user">Users</option>
                      <option value="astrologer">Astrologers</option>
                    </select>
                  </div>
                </div>

                {/* Title */}
                <div className="mb-4">
                  <label className="form-label fw-bold">Title <span className="text-danger">*</span></label>
                  <input type="text" className="form-control" placeholder="Enter Title" {...formik.getFieldProps("title")} />
                  {formik.touched.title && formik.errors.title && (
                    <small className="text-danger">{formik.errors.title}</small>
                  )}
                </div>

                {/* Description */}
                <div className="mb-4">
                  <label className="form-label fw-bold">Description <span className="text-danger">*</span></label>
                  <CustomCkeditor
                    fieldname="description"
                    setFieldValue={formik.setFieldValue}
                    setFieldTouched={formik.setFieldTouched}
                    value={formik.values.description}
                  />
                  {formik.touched.description && formik.errors.description && (
                    <small className="text-danger">{formik.errors.description}</small>
                  )}
                </div>

                {/* Thumbnail */}
                <div className="mb-4">
                  <label className="form-label fw-bold">Thumbnail <span className="text-danger">*</span></label>
                  <input
                    type="file"
                    accept="image/*"
                    className="form-control"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      formik.setFieldValue("thumbnail", file);
                      setPreviewThumbnail(file ? URL.createObjectURL(file) : "");
                    }}
                  />
                  {previewThumbnail && (
                    <div className="mt-3 text-center">
                      <img src={previewThumbnail} alt="Preview" className="img-thumbnail" style={{ maxHeight: "200px" }} />
                    </div>
                  )}
                  {formik.touched.thumbnail && formik.errors.thumbnail && (
                    <small className="text-danger">{formik.errors.thumbnail}</small>
                  )}
                </div>

                {/* Video Section */}
                {formik.values.content_type === "lesson" && (
                  <div className="border rounded p-4 bg-light mb-4">
                    <h6 className="fw-bold mb-3">Video Source <span className="text-danger">*</span></h6>

                    <div className="row mb-4">
                      <div className="col-md-6">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            value="upload"
                            checked={formik.values.video_source === "upload"}
                            onChange={() => formik.setFieldValue("video_source", "upload")}
                          />
                          <label className="form-check-label fw-bold">Upload Video</label>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            value="youtube"
                            checked={formik.values.video_source === "youtube"}
                            onChange={() => {
                              formik.setFieldValue("video_source", "youtube");
                              formik.setFieldValue("video_file", null);
                            }}
                          />
                          <label className="form-check-label fw-bold">YouTube Link</label>
                        </div>
                      </div>
                    </div>

                    {/* Upload Video */}
                    {/* {formik.values.video_source === "upload" && (
                      <div>
                        <label className="form-label">Upload Video <span className="text-danger">*</span></label>
                        <input
                          type="file"
                          accept="video/*"
                          className="form-control"
                          onChange={(e) => formik.setFieldValue("video_file", e.target.files[0] || null)}
                        />
                        {formik.values.video_file && (
                          <small className="text-success d-block mt-2">
                            Selected: <strong>{formik.values.video_file.name}</strong>
                          </small>
                        )}
                        {formik.touched.video_file && formik.errors.video_file && (
                          <small className="text-danger">{formik.errors.video_file}</small>
                        )}
                      </div>
                    )} */}
                    {formik.values.video_source === "upload" && (
                    <div>
                      <label className="form-label fw-bold">
                        Upload Video <span className="text-danger">*</span>
                      </label>

                      <input
                        type="file"
                        accept="video/mp4,.mp4"  // Browser only shows MP4 files
                        className={`form-control ${
                          formik.touched.video_file && formik.errors.video_file ? "is-invalid" : ""
                        }`}
                        onChange={(e) => {
                          const file = e.target.files[0];

                          // Reset previous errors
                          formik.setFieldError("video_file", undefined);

                          if (!file) {
                            formik.setFieldValue("video_file", null);
                            return;
                          }

                          // Strict MP4 validation
                          const isValidType = file.type === "video/mp4";
                          const isValidExtension = file.name.toLowerCase().endsWith(".mp4");

                          if (!isValidType || !isValidExtension) {
                            formik.setFieldValue("video_file", null);
                            formik.setFieldError(
                              "video_file",
                              "Invalid file. Only MP4 video files (.mp4) are allowed."
                            );

                            Swal.fire({
                              icon: "warning",
                              title: "Invalid File Type",
                              text: "Please upload only MP4 video files. Other formats (MOV, AVI, WebM, images, etc.) are not supported.",
                              ...SWAL_SETTINGS,
                            });
                            return;
                          }

                          // Valid MP4 → accept
                          formik.setFieldValue("video_file", file);
                        }}
                      />

                      {/* Show selected file info */}
                      {formik.values.video_file && (
                        <div className="mt-3 p-3 bg-success bg-opacity-10 border border-success rounded">
                          <small className="text-success">
                            <strong>Selected:</strong> {formik.values.video_file.name}
                          </small>
                          <br />
                          <small className="text-muted">
                            Size: {(formik.values.video_file.size / (1024 * 1024)).toFixed(2)} MB • Type: MP4
                          </small>
                        </div>
                      )}

                      {/* Formik Error */}
                      {formik.touched.video_file && formik.errors.video_file && (
                        <div className="invalid-feedback d-block mt-2">
                          {formik.errors.video_file}
                        </div>
                      )}

                      {/* Help Text */}
                      <small className="text-muted d-block mt-3">
                        <strong>Only MP4 video files (.mp4) are allowed.</strong><br />
                        Files like images, PDFs, MOV, AVI, WebM, MKV will be rejected.
                      </small>
                    </div>
                  )}

                    {/* YouTube Link */}
                    {formik.values.video_source === "youtube" && (
                      <div>
                        <label className="form-label">YouTube URL <span className="text-danger">*</span></label>
                        <input
                          type="url"
                          className="form-control"
                          placeholder="https://www.youtube.com/watch?v=..."
                          {...formik.getFieldProps("video_link")}
                        />
                        {formik.touched.video_link && formik.errors.video_link && (
                          <small className="text-danger">{formik.errors.video_link}</small>
                        )}
                      </div>
                    )}

                    {/* Progress */}
                    {uploading && (
                      <div className="mt-4">
                        <div className="progress" style={{ height: "40px" }}>
                          <div
                            className="progress-bar progress-bar-striped progress-bar-animated bg-success"
                            style={{ width: `${uploadProgress}%` }}
                          >
                            <strong>{uploadProgress}% Uploading...</strong>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Submit */}
                <div className="mt-5 d-flex gap-3">
                  <button
                    type="submit"
                    disabled={uploading || formik.isSubmitting}
                    className="btn btn-primary btn-lg px-5"
                  >
                    {uploading ? `Saving... ${uploadProgress}%` : "Save Content"}
                  </button>
                  <button type="button" onClick={() => navigate(-1)} className="btn btn-secondary btn-lg px-5">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default AddLearnHub;